/*
 * Adding, changing or stopping one of a resident's medicines — or editing a draft of one.
 *
 * Whatever is saved here is a draft: the home does not see it until the pharmacy approves it. The
 * checks that belong at the moment of typing stay here: the allergy check as the name is typed,
 * picking from the NHS dictionary rather than spelling it, the controlled drug status and tray
 * eligibility dm+d already knows, and the kind of medicine that decides what the round asks for.
 *
 * When it starts has to be chosen, never assumed. Defaulting to today would put next cycle's set-up
 * on the chart the moment it is approved; defaulting to next cycle would leave today's antibiotic
 * off it. Either mistake is easy to make and hard to see, so the form asks.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import type { CycleDates, Draft, Medicine, Report } from './medicine-types.ts';
import { SOURCES } from './medicine-types.ts';
import { Overlay } from './overlay.tsx';
import { SlotEditor, emptySlot } from './schedule-builder.tsx';
import type { Slot } from './schedule-builder.tsx';
import { shortDay } from './days.ts';

type Product = {
  id: string;
  name: string;
  form: string;
  strength: string;
  route: string;
  controlledDrug: boolean;
  cdSchedule: string;
  usualTray: boolean;
  notTrayReason: string;
  fromDictionary: boolean;
};

export type FormMode =
  | { kind: 'new' }
  | { kind: 'change'; medicine: Medicine }
  | { kind: 'stop'; medicine: Medicine }
  | { kind: 'edit'; draft: Draft };

type Props = {
  homeId: string;
  residentId: string;
  residentName: string;
  allergies: string[];
  mode: FormMode;
  cycle: CycleDates | null;
  today: string;
  // Making a change the home reported: shown at the top, and its source carried over.
  reported?: Report | null;
  onClose: () => void;
  onDone: () => void;
};

/* The fields the form starts from: a medicine being changed, a draft being edited, or nothing. */
function startingPoint(mode: FormMode): Partial<Draft> & { kind: 'new' | 'change' | 'stop'; medicineId: string | null } {
  if (mode.kind === 'new') return { kind: 'new', medicineId: null };
  if (mode.kind === 'edit') return { ...mode.draft };
  const { id, startsOn: _startsOn, endsOn: _endsOn, draft: _draft, ...rest } = mode.medicine;
  void _startsOn; void _endsOn; void _draft;
  return { ...rest, kind: mode.kind, medicineId: id };
}

export function MedicineForm({
  homeId, residentId, residentName, allergies, mode, cycle, today, reported, onClose, onDone,
}: Props) {
  const start = startingPoint(mode);
  const kind = start.kind;
  const editing = mode.kind === 'edit' ? mode.draft : null;
  const current = mode.kind === 'change' || mode.kind === 'stop' ? mode.medicine : null;

  const [source, setSource] = useState(start.source ?? reported?.source ?? 'gp');
  const [sourceDetail, setSourceDetail] = useState(start.sourceDetail ?? reported?.sourceDetail ?? '');
  const [name, setName] = useState(start.name ?? '');
  const [productId, setProductId] = useState(start.productId ?? '');
  const [doseText, setDoseText] = useState(start.doseText ?? '');
  const [directions, setDirections] = useState(start.directions ?? '');
  const [slots, setSlots] = useState<Slot[]>(
    start.slots?.length
      ? start.slots
      : start.times?.length
        ? start.times.map((time) => ({ ...emptySlot(time), doseText: start.doseText ?? '' }))
        : [emptySlot()],
  );
  const [prn, setPrn] = useState(start.prn ?? false);
  const [trayType, setTrayType] = useState<'tray' | 'separate' | 'none'>(start.trayType ?? 'tray');
  const [trayLooks, setTrayLooks] = useState(start.trayLooks ?? '');
  const [notTrayReason, setNotTrayReason] = useState(start.notTrayReason ?? '');
  const [stopReason, setStopReason] = useState(start.stopReason ?? '');
  const [note, setNote] = useState(start.note ?? '');
  const [controlledDrug, setControlledDrug] = useState(start.controlledDrug ?? false);
  const [cdSchedule, setCdSchedule] = useState('');
  const [timeCritical, setTimeCritical] = useState(start.timeCritical ?? false);
  const [nurseOnly, setNurseOnly] = useState(start.nurseOnly ?? false);
  const [topical, setTopical] = useState(start.topical ?? false);
  const [insulin, setInsulin] = useState(Boolean(start.flags?.insulin));
  const [warfarin, setWarfarin] = useState(Boolean(start.flags?.warfarin));
  const [peg, setPeg] = useState(Boolean(start.flags?.peg));
  const [patch, setPatch] = useState(Boolean(start.flags?.patch));
  // Only a draft being edited arrives with a date already chosen. Everything else has to be asked.
  const [startsOn, setStartsOn] = useState(editing?.startsOn ?? '');
  const [endsOn, setEndsOn] = useState(start.endsOn ?? '');

  const [warning, setWarning] = useState('');
  const [picks, setPicks] = useState<Product[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  /* Look the medicine up as it is typed, so it can be picked rather than spelled. */
  useEffect(() => {
    if (kind === 'stop' || productId || name.trim().length < 2) {
      setPicks([]);
      return;
    }
    const timer = setTimeout(() => {
      api
        .get<{ products: Product[] }>(`/api/products?q=${encodeURIComponent(name)}`)
        .then((data) => setPicks(data.products))
        .catch(() => setPicks([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [name, kind, productId]);

  // The allergy check runs as the name is typed, so it is seen before anything is saved.
  useEffect(() => {
    if (!name.trim() || kind === 'stop') {
      setWarning('');
      return;
    }
    const timer = setTimeout(() => {
      api
        .post<{ warning: string }>('/api/allergy-check', { residentId, name })
        .then((data) => setWarning(data.warning))
        .catch(() => setWarning(''));
    }, 350);
    return () => clearTimeout(timer);
  }, [residentId, name, kind]);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!startsOn) {
        setError(kind === 'stop' ? 'Say when it stops.' : 'Say when it starts.');
        return;
      }
      setBusy(true);
      setError('');
      try {
        await api.post(`/api/homes/${homeId}/changes`, {
          residentId,
          kind,
          medicineId: kind === 'new' ? undefined : start.medicineId ?? undefined,
          replacesDraftId: editing?.id,
          source,
          sourceDetail,
          name,
          doseText,
          directions,
          slots: prn ? [] : slots,
          productId: productId || undefined,
          prn,
          trayType: prn ? 'none' : trayType,
          trayLooks,
          notTrayReason: prn && !notTrayReason ? 'When required, so not in the tray' : notTrayReason,
          stopReason,
          note,
          controlledDrug,
          timeCritical,
          nurseOnly,
          topical: topical || patch,
          flags: { insulin, warfarin, peg, patch },
          doseQty: slots[0]?.qty ?? undefined,
          startsOn,
          endsOn: kind === 'stop' ? undefined : endsOn || undefined,
        });
        onDone();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [homeId, residentId, kind, start.medicineId, editing, source, sourceDetail, name, doseText, directions, slots,
      prn, productId, trayType, trayLooks, notTrayReason, stopReason, note, controlledDrug, timeCritical,
      nurseOnly, topical, insulin, warfarin, peg, patch, startsOn, endsOn, onDone],
  );

  const title =
    mode.kind === 'new' ? 'Add a medicine'
    : mode.kind === 'change' ? `Change ${mode.medicine.name}`
    : mode.kind === 'stop' ? `Stop ${mode.medicine.name}`
    : `Edit the draft: ${mode.draft.kind === 'new' ? mode.draft.name : mode.draft.medicineName || mode.draft.name}`;

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>{title}</h2>
        <p className="muted">
          For {residentName}. This is saved as a draft: the home will not see it until it is approved.
        </p>

        {reported ? (
          <p className="notice">
            <strong>The home reported: {reported.whatLabel}.</strong> "{reported.detail}" — {reported.reportedBy}
            {reported.sourceDetail ? `, from ${reported.sourceDetail}` : ''}. Check it against the prescription.
          </p>
        ) : null}

        {allergies.length ? <p className="notice warn">Allergic to: {allergies.join(', ')}</p> : null}

        {current ? (
          <div className="now-on">
            <span className="muted small">On the chart now</span>
            <span className="row-main">{current.name}</span>
            <span className="small">{current.directions}</span>
          </div>
        ) : null}

        <WhenPicker
          label={kind === 'stop' ? 'Stops from' : kind === 'change' ? 'The new directions start' : 'Starts'}
          value={startsOn}
          onChange={setStartsOn}
          today={today}
          cycle={cycle}
        />
        {kind === 'stop' ? (
          <p className="muted small">The last dose is the day before. From today means it comes off the round straight away once approved.</p>
        ) : null}

        <label htmlFor="m-source">Where it came from</label>
        <select id="m-source" value={source} onChange={(e) => setSource(e.target.value)}>
          {SOURCES.map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>

        <label htmlFor="m-source-detail">Who said so, and when</label>
        <input id="m-source-detail" value={sourceDetail} onChange={(e) => setSourceDetail(e.target.value)}
          placeholder="Dr Shah, prescription dated 18 September" />

        {kind !== 'stop' ? (
          <>
            <label htmlFor="m-name">Medicine, strength and form</label>
            <input id="m-name" required value={name} autoComplete="off"
              onChange={(e) => {
                setName(e.target.value);
                setProductId('');
              }}
              placeholder="Start typing: amlod…" />

            {picks.length && !productId ? (
              <ul className="picker">
                {picks.map((product) => (
                  <li key={product.id}>
                    <button type="button" onClick={() => {
                      setName(product.name);
                      setProductId(product.id);
                      setPicks([]);
                      // dm+d already knows this; do not make somebody remember it.
                      setControlledDrug(product.controlledDrug);
                      setCdSchedule(product.cdSchedule);
                      if (!product.usualTray) {
                        setTrayType('none');
                        setNotTrayReason(product.notTrayReason);
                      }
                    }}>
                      <span className="row-main">{product.name}</span>
                      <span className="chips">
                        {product.controlledDrug ? (
                          <span className="chip warn">Controlled drug{product.cdSchedule ? ` (sch ${product.cdSchedule})` : ''}</span>
                        ) : null}
                        {!product.usualTray ? <span className="chip">{product.notTrayReason}</span> : null}
                        {product.fromDictionary ? <span className="chip">dm+d</span> : null}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {name && !productId ? (
              <p className="muted small">
                Not picked from the list, so this will be recorded exactly as typed. Check the spelling, the
                strength and the form against the prescription.
              </p>
            ) : null}

            {warning ? <p className="notice warn">{warning}</p> : null}

            <label htmlFor="m-dose">Dose</label>
            <input id="m-dose" value={doseText} onChange={(e) => setDoseText(e.target.value)} placeholder="ONE tablet" />

            <label htmlFor="m-directions">Directions, exactly as on the prescription</label>
            <input id="m-directions" required value={directions} onChange={(e) => setDirections(e.target.value)}
              placeholder="Take ONE tablet each morning" />

            <label className="check" htmlFor="m-prn">
              <input id="m-prn" type="checkbox" checked={prn} onChange={(e) => setPrn(e.target.checked)} />
              <span><strong>When required</strong><span className="muted small"> no set times</span></span>
            </label>

            {!prn ? <SlotEditor slots={slots} onChange={setSlots} /> : null}

            <label htmlFor="m-ends">Last day (optional)</label>
            <input id="m-ends" type="date" value={endsOn} min={startsOn || undefined} onChange={(e) => setEndsOn(e.target.value)} />
            <p className="muted small">
              For a course, such as an antibiotic. It comes off the round by itself after this day, so nobody has
              to remember to stop it.
            </p>

            <fieldset>
              <legend>What kind of medicine is this</legend>
              <p className="muted small">
                These decide what the round asks for. Get them wrong and the checks never happen: no blood
                glucose before insulin, no INR before warfarin, no rested skin before a patch.
              </p>

              <label className="check" htmlFor="m-cd">
                <input id="m-cd" type="checkbox" checked={controlledDrug} onChange={(e) => setControlledDrug(e.target.checked)} />
                <span>
                  <strong>Controlled drug</strong>
                  <span className="muted small">
                    {' '}two people at every dose, and it goes on the register
                    {cdSchedule ? ` · schedule ${cdSchedule} from dm+d` : ''}
                  </span>
                </span>
              </label>
              <label className="check" htmlFor="m-critical">
                <input id="m-critical" type="checkbox" checked={timeCritical} onChange={(e) => setTimeCritical(e.target.checked)} />
                <span>
                  <strong>Time critical</strong>
                  <span className="muted small"> Parkinson's, insulin, epilepsy — late is flagged as late</span>
                </span>
              </label>
              <label className="check" htmlFor="m-nurse">
                <input id="m-nurse" type="checkbox" checked={nurseOnly} onChange={(e) => setNurseOnly(e.target.checked)} />
                <span><strong>Nurses only</strong><span className="muted small"> carers cannot record it</span></span>
              </label>
              <label className="check" htmlFor="m-topical">
                <input id="m-topical" type="checkbox" checked={topical} onChange={(e) => setTopical(e.target.checked)} />
                <span>
                  <strong>A cream or ointment</strong>
                  <span className="muted small"> recorded on the body map, so it asks where it went on</span>
                </span>
              </label>
              <label className="check" htmlFor="m-patch">
                <input id="m-patch" type="checkbox" checked={patch} onChange={(e) => setPatch(e.target.checked)} />
                <span>
                  <strong>A patch</strong>
                  <span className="muted small"> asks where it went and refuses skin used in the last 14 days</span>
                </span>
              </label>
              <label className="check" htmlFor="m-insulin">
                <input id="m-insulin" type="checkbox" checked={insulin} onChange={(e) => setInsulin(e.target.checked)} />
                <span>
                  <strong>Insulin</strong>
                  <span className="muted small"> asks for a blood glucose first, and a hypo stops the dose</span>
                </span>
              </label>
              <label className="check" htmlFor="m-warfarin">
                <input id="m-warfarin" type="checkbox" checked={warfarin} onChange={(e) => setWarfarin(e.target.checked)} />
                <span>
                  <strong>Warfarin</strong>
                  <span className="muted small"> will not be given without a current INR and the clinic's dose</span>
                </span>
              </label>
              <label className="check" htmlFor="m-peg">
                <input id="m-peg" type="checkbox" checked={peg} onChange={(e) => setPeg(e.target.checked)} />
                <span>
                  <strong>Goes down a PEG tube</strong>
                  <span className="muted small"> the nurse works through the tube checks first</span>
                </span>
              </label>
            </fieldset>

            <fieldset>
              <legend>Where this medicine lives</legend>
              <label className="check" htmlFor="m-tray">
                <input id="m-tray" type="radio" name="m-tray-type" checked={trayType === 'tray'} onChange={() => setTrayType('tray')} />
                <span>
                  <strong>In the tray</strong>
                  <span className="muted small"> blistered with everything else at that time of day</span>
                </span>
              </label>
              <label className="check" htmlFor="m-separate">
                <input id="m-separate" type="radio" name="m-tray-type" checked={trayType === 'separate'} onChange={() => setTrayType('separate')} />
                <span>
                  <strong>In a tray of its own</strong>
                  <span className="muted small"> a liquid, or anything that cannot share a blister</span>
                </span>
              </label>
              <label className="check" htmlFor="m-original">
                <input id="m-original" type="radio" name="m-tray-type" checked={trayType === 'none'} onChange={() => setTrayType('none')} />
                <span>
                  <strong>Original pack</strong>
                  <span className="muted small"> creams, patches, inhalers, when-required and controlled drugs</span>
                </span>
              </label>

              {trayType === 'none' ? (
                <>
                  <label htmlFor="m-not-tray">Why it stays out of the tray</label>
                  <input id="m-not-tray" value={notTrayReason} onChange={(e) => setNotTrayReason(e.target.value)}
                    placeholder="When required, so not in the tray" />
                </>
              ) : (
                <>
                  <label htmlFor="m-looks">What it looks like, for the carer checking the blister</label>
                  <input id="m-looks" value={trayLooks} onChange={(e) => setTrayLooks(e.target.value)}
                    placeholder="small white round tablet" />
                </>
              )}
            </fieldset>
          </>
        ) : (
          <>
            <label htmlFor="m-stop">Why is it stopping?</label>
            <input id="m-stop" required value={stopReason} onChange={(e) => setStopReason(e.target.value)}
              placeholder="GP stopped it at review" />
          </>
        )}

        <label htmlFor="m-note">A note for whoever approves it (optional)</label>
        <input id="m-note" value={note} onChange={(e) => setNote(e.target.value)} />

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save as draft'}</button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

/*
 * When a change takes effect: from today, from the start of next cycle, or a date. Nothing is
 * chosen until someone chooses it.
 */
function WhenPicker({
  label, value, onChange, today, cycle,
}: { label: string; value: string; onChange: (value: string) => void; today: string; cycle: CycleDates | null }) {
  const next = cycle?.nextStartsOn ?? '';
  const choice = value === today ? 'today' : next && value === next ? 'next' : value ? 'date' : '';
  return (
    <fieldset className="when">
      <legend>{label}</legend>
      <div className="when-options">
        <button type="button" className={choice === 'today' ? 'on' : 'quiet'} aria-pressed={choice === 'today'} onClick={() => onChange(today)}>
          Today
        </button>
        {next ? (
          <button type="button" className={choice === 'next' ? 'on' : 'quiet'} aria-pressed={choice === 'next'} onClick={() => onChange(next)}>
            Start of next cycle · {shortDay(next)}
          </button>
        ) : null}
        <input
          aria-label={`${label}: a date`}
          type="date"
          value={value}
          min={today}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      {!value ? <p className="muted small">Choose one.</p> : null}
    </fieldset>
  );
}
